import React, { useContext, useEffect, useState } from 'react';
import { RolesContext } from '../../../contexts/RolesContext';
import { InspectionContext } from '../../../contexts/InspectionContext';
import AppContext from '../../../contexts/AppContext';
import { TooltipContextProvider } from '@stormwater-software/lib-ui';
import WebViewerModal from '../../Shared/PDFEditor/WebViewerModal';
import SuccessModal from '../../Shared/SuccessModal/SuccessModal';
import FileCardList from '../../Shared/FileRow/List';
import uploadApi, { fileToBase64 } from '../../../utils/api/upload';
import { toast } from 'react-toastify';
import FileRenameModal from '../../Shared/FileRow/FileRenameModal';
import { useOffline } from '../../../contexts/OfflineContext';
import db, { offlineImageCache } from '../../../db/db';
import { getDateOnly } from '../../../utils/date';
import { v4 as uuidv4 } from 'uuid';

const allowedTypes = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/pdf',
];

const InspectionDocumentUploads = () => {
  const rolesContext = useContext(RolesContext);
  const inspectionContext = useContext(InspectionContext);
  const inspectionId = inspectionContext.inspection.id;
  const appContext = useContext(AppContext);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [showDownload, setShowDownload] = useState<boolean>(false);
  const [rename, setRename] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('');
  const [docGuid, setDocGuid] = useState<string>('');
  const [docId, setDocId] = useState<number>(-1);
  const [errorText, setErrorText] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [shouldReloadDocumentsPage, setShouldReloadDocumentsPage] =
    useState(false);

  const isOffline = useOffline();

  useEffect(() => {
    setIsAdmin(appContext.get('user').roleName === 'Admin');
  }, []);

  useEffect(() => {
    inspectionContext.loadDocs(inspectionId);
  }, [shouldReloadDocumentsPage]);

  const hideEditor = () => {
    setShowEditor(false);
  };

  const showRename = async (document: any) => {
    setRename(true);
    setDocGuid(document.upload.GUID);
    setDocId(document.upload.id);
    setFilename(document.upload.name);
  };

  const hideRename = () => {
    setDocGuid('');
    setDocId(-1);
    setFilename('');
    setRename(false);
  };

  const onEdit = () => {
    Promise.resolve();
  };

  const uploadOffline = async (newFile: any, online = false) => {
    const guid = uuidv4();

    const base64File = await fileToBase64(newFile);

    const inspection = await db.inspections.get(inspectionId);

    if (!inspection) {
      console.error('Inspection not found in offline database');

      return;
    }

    // The raw File is deliberately not persisted here: iOS stores it as a
    // reference into Safari's temp area, and once iOS reclaims that file the
    // row still reads back as a File with no bytes behind it, so the sync sent
    // an empty multipart body and the API answered 400 "No file attached". The
    // Base64 copy below is the durable one and sync rebuilds the File from it.
    const syncObj = {
      synced: false,
      payload: {
        inspection_id: inspectionId,
      },
    };

    if (online) {
      syncObj.synced = true;
    }

    db.images.add({
      guid: guid,
      file: base64File,
      type: newFile.type,
      name: newFile.name,
      payload_id: inspectionId,
      sync: {
        ...syncObj,
      },
    });

    offlineImageCache[guid] = base64File;

    const newDoc = {
      createdAt: getDateOnly(new Date()),
      updatedAt: getDateOnly(new Date()),
      originalCreatedDate: getDateOnly(new Date()),
      deletedAt: null,
      inspection_id: inspectionId,
      id: guid,
      upload: {
        GUID: guid,
        mime_type: newFile.type,
        name: newFile.name,
        timestamp: new Date(),
        gps_latitude: null,
        gps_longitude: null,
        archived_at: null,
        createdAt: getDateOnly(new Date()),
        updatedAt: getDateOnly(new Date()),
        deletedAt: null,
      },
    };

    if (!online) {
      inspectionContext.setInspectionDocs((prevDocs: any) => {
        return [...prevDocs, newDoc];
      });
    }

    const inspectionUpload = await db.inspectionUploads.get(inspectionId);

    if (!inspectionUpload) {
      await db.inspectionUploads.add({
        inspection_id: inspectionId,
        uploads: [newDoc],
      });
    } else {
      await db.inspectionUploads
        .where({ inspection_id: inspectionId })
        .modify((doc: any) => {
          doc.uploads.push(newDoc);
        });
    }
  };

  const handleUpload = async (newFile: any) => {
    if (!isOffline) {
      return uploadApi.inspection
        .create(newFile.id, inspectionId)
        .then(() => {
          // uploadOffline(newFile,true);
          inspectionContext.loadDocs(inspectionId);

          return;
        })
        .catch((err: any) => {
          setErrorText(
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'An error has occurred',
          );
          setShowErrorModal(true);
        });
    } else {
      uploadOffline(newFile);
    }
  };

  const handleDownload = async (doc: any) => {
    toast('Download has started');

    if (!isOffline) {
      setShowDownload(true);
      setDocGuid(doc.upload.GUID);
      setDocId(doc.upload.id);
      setFilename(doc.upload.name);
    } else {
      const file = await db.images.get(doc.upload.GUID);

      setDocGuid(doc.upload.GUID);
      setDocId(doc.upload.id);
      setFilename(doc.upload.name);

      const a = document.createElement('a');

      a.href = file.file;
      a.download = doc.upload.name;
      a.click();
      window.URL.revokeObjectURL(file.file);
    }
  };

  const handleEdit = async (doc: any) => {
    setShowEditor(true);
    setDocGuid(doc.upload.GUID);
    setDocId(doc.upload.id);
    setFilename(doc.upload.name);
  };

  const handleReplace = async (newDoc: any, doc: any) => {
    try {
      const response = await uploadApi.create(newDoc);

      const payload = {
        newDocId: response.id,
        oldDocId: doc.upload.id,
      };

      await uploadApi.inspection.replace(inspectionId, payload);
      inspectionContext.loadDocs(inspectionId);
    } catch (err) {
      setErrorText(
        err?.response?.data?.message
          ? err.response.data.message
          : 'An error has occurred',
      );
      setShowErrorModal(true);
    }
  };

  const handleRename = async (newName: any) => {
    await uploadApi.rename(docGuid, newName).then(() => {
      inspectionContext.loadDocs(inspectionId);
    });
    hideRename();
  };

  const handleDelete = async (doc: any) => {
    uploadApi.inspection
      .destroy(doc.upload.id, inspectionId)
      .then(() => {
        inspectionContext.loadDocs(inspectionId);
      })
      .catch((err) => {
        setErrorText(
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'An error has occurred',
        );
        setShowErrorModal(true);
      });
  };

  return (
    <TooltipContextProvider>
      <div className='inspection-documents-container'>
        <FileCardList
          key={0}
          groupName={'Inspection Attachments'}
          permCheck={rolesContext.userHasPermission}
          documents={inspectionContext.inspectionDocs}
          canDownload={rolesContext.userHasPermission('all', 'Download')}
          canEdit={rolesContext.userHasPermission('all', 'Inspection Uploads')}
          canDelete={
            rolesContext.userHasPermission('all', 'Inspection Uploads') &&
            !isOffline
          }
          canReplace={
            rolesContext.userHasPermission('all', 'Inspection Uploads') &&
            !isOffline
          }
          canRename={
            rolesContext.userHasPermission('all', 'Inspection Uploads') &&
            !isOffline
          }
          canUpload={rolesContext.userHasPermission(
            'all',
            'Inspection Uploads',
          )}
          canDrag={
            rolesContext.userHasPermission('all', 'Inspection Uploads') &&
            !isOffline
          }
          onUpload={async (newFile: any) => handleUpload(newFile)}
          handleDownload={(doc: any) => handleDownload(doc)}
          handleEdit={(doc: any) => handleEdit(doc)}
          handleReplace={async (newDoc: any, doc: any) =>
            handleReplace(newDoc, doc)
          }
          handleRename={async (doc: any) => {
            await showRename(doc);
          }}
          handleDelete={(doc: any) => handleDelete(doc)}
          allowedTypes={allowedTypes}
          setShouldReloadDocumentsPage={setShouldReloadDocumentsPage}
          document_date={true}
          isAdmin={isAdmin}
          isInspectionAttachment={true}
          label='Inspection Uploads'
        />
      </div>
      <WebViewerModal
        show={showEditor}
        onClose={hideEditor}
        onSave={() => onEdit()}
        guid={docGuid}
        id={docId}
        uploadId={docId}
        filename={filename}
        disableDownload={!rolesContext.userHasPermission('all', 'Download')}
        canEdit={rolesContext.userHasPermission('update', 'Project Documents')}
      />

      <WebViewerModal
        show={showDownload}
        onClose={hideEditor}
        onSave={() => onEdit()}
        guid={docGuid}
        id={docId}
        uploadId={docId}
        canDownload={true}
        filename={filename}
        canEdit={rolesContext.userHasPermission('update', 'Project Documents')}
      />

      {/* invalid file type || DB error */}
      <SuccessModal
        show={showErrorModal}
        handleClose={() => setShowErrorModal(false)}
        handleSubmit={() => setShowErrorModal(false)}
        submitBtnText='OK'
        message={errorText}
        title='Error'
        isAlert
      />

      <FileRenameModal
        show={rename}
        file={{ guid: docGuid, name: filename }}
        onCancel={hideRename}
        onSubmit={(fileName) => handleRename(fileName)}
      />
    </TooltipContextProvider>
  );
};

export default InspectionDocumentUploads;
